import React, { useEffect, useState } from 'react';
import {
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Box, Button, TextField, MenuItem, Select, FormControl, InputLabel, Checkbox, Alert
} from '@mui/material';
import api from '../../services/api';
import { ArrowBack } from '@mui/icons-material';
import { getCurrentBSDate } from '../../utils/dateUtils';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];

const ItemIssue = () => {
  const [rows, setRows] = useState([]);
  const [karigarFilter, setKarigarFilter] = useState('');
  const [karatFilter, setKaratFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('both');
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [issueDetails, setIssueDetails] = useState({});
  const [selected, setSelected] = useState([]);
  const [undespatching, setUndespatching] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, directOrdersRes, karigarsRes] = await Promise.all([
        api.get('/api/shop/orders'),
        api.get('/api/directorders'),
        api.get('/api/karigar')
      ]);
      setKarigars(karigarsRes.data);

      // Karigar item issue: items despatched via KARIGAR despatch (despatchStatus:true, karigarAbbreviation set)
      const itemRows = [
        ...ordersRes.data.flatMap(order =>
          (order.items || [])
            .filter(item => item.despatchStatus && item.karigarAbbreviation && !item.workIssued)
            .map(item => ({
              billName: order.billName,
              billNumber: order.billNumber,
              itemName: item.itemName,
              karigar: item.karigarAbbreviation || '',
              karat: item.karat,
              itemWeight: item.itemWeight,
              quantity: item.quantity,
              orderId: order._id,
              itemId: item._id,
              isDirect: false
            }))
        ),
        ...directOrdersRes.data.flatMap(order =>
          (order.items || [])
            .filter(item => item.despatchStatus && item.karigarAbbreviation && !item.workIssued)
            .map(item => ({
              billName: order.billName || 'D',
              billNumber: order.billNumber,
              itemName: item.itemName,
              karigar: item.karigarAbbreviation || '',
              karat: item.karat,
              itemWeight: item.itemWeight,
              quantity: item.quantity,
              orderId: order._id,
              itemId: item._id,
              isDirect: true
            }))
        )
      ];
      setRows(itemRows);
    } catch (err) {
      setError('Failed to fetch data');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleInputChange = (itemKey, field, value) => {
    setIssueDetails(d => ({ ...d, [itemKey]: { ...d[itemKey], [field]: value } }));
  };

  const handleSelect = (itemKey, checked) => {
    setSelected(sel => {
      if (checked) {
        setIssueDetails(d => ({ ...d, [itemKey]: { ...d[itemKey], date: getCurrentBSDate() } }));
        return [...sel, itemKey];
      }
      return sel.filter(k => k !== itemKey);
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelected(filteredRows.map(r => r.orderId + '-' + r.itemId));
    else setSelected([]);
  };

  const handleIssueSelected = async () => {
    try {
      const itemsByOrder = {};
      const itemsByDirectOrder = {};
      selected.forEach(itemKey => {
        const row = rows.find(r => r.orderId + '-' + r.itemId === itemKey);
        if (!row) return;
        if (row.isDirect) {
          if (!itemsByDirectOrder[row.orderId]) itemsByDirectOrder[row.orderId] = [];
          itemsByDirectOrder[row.orderId].push(row.itemId);
        } else {
          if (!itemsByOrder[row.orderId]) itemsByOrder[row.orderId] = [];
          itemsByOrder[row.orderId].push(row.itemId);
        }
      });
      for (const orderId of Object.keys(itemsByOrder)) {
        const orderRes = await api.get(`/api/shop/orders/${orderId}`);
        const order = orderRes.data;
        const updatedItems = order.items.map(item => {
          const itemKey = orderId + '-' + item._id;
          if (itemsByOrder[orderId].includes(String(item._id))) {
            return { ...item, workIssued: true, issueDate: issueDetails[itemKey]?.date || getCurrentBSDate(), issueRemarks: issueDetails[itemKey]?.remarks || '' };
          }
          return item;
        });
        await api.put(`/api/shop/orders/${orderId}`, { ...order, items: updatedItems });
      }
      for (const orderId of Object.keys(itemsByDirectOrder)) {
        const orderRes = await api.get(`/api/directorders/${orderId}`);
        const order = orderRes.data;
        const updatedItems = order.items.map(item => {
          const itemKey = orderId + '-' + item._id;
          if (itemsByDirectOrder[orderId].includes(String(item._id))) {
            return { ...item, workIssued: true, issueDate: issueDetails[itemKey]?.date || getCurrentBSDate(), issueRemarks: issueDetails[itemKey]?.remarks || '' };
          }
          return item;
        });
        await api.put(`/api/directorders/${orderId}`, { ...order, items: updatedItems });
      }
      setSelected([]);
      await fetchData();
    } catch (err) {
      setError('Failed to issue selected items');
    }
  };

  const handleUndespatch = async (row) => {
    const key = row.orderId + '-' + row.itemId;
    setUndespatching(key);
    setError('');
    try {
      await api.post('/api/karigar/undespatch', { orderId: row.orderId, itemId: row.itemId, isDirect: row.isDirect });
      await fetchData();
    } catch (err) {
      setError('Failed to undespatch item');
    }
    setUndespatching(null);
  };

  const filteredRows = rows.filter(row => {
    const karigarMatch = !karigarFilter || row.karigar === karigarFilter;
    const karatMatch = !karatFilter || row.karat === karatFilter;
    const sourceMatch = sourceFilter === 'both' ? true : sourceFilter === 'order' ? !row.isDirect : row.isDirect;
    return karigarMatch && karatMatch && sourceMatch;
  });
  const allSelected = filteredRows.length > 0 && filteredRows.every(r => selected.includes(r.orderId + '-' + r.itemId));

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" color="primary" sx={{ mb: 2 }}>Back</Button>
        <Typography variant="h4" gutterBottom color="primary">Item Issue Management</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>Source</InputLabel>
            <Select value={sourceFilter} label="Source" onChange={e => setSourceFilter(e.target.value)}>
              <MenuItem value="both">Both</MenuItem>
              <MenuItem value="order">Order</MenuItem>
              <MenuItem value="direct">Direct Order</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>Karigar</InputLabel>
            <Select value={karigarFilter} label="Karigar" onChange={e => setKarigarFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {karigars.map(k => <MenuItem key={k.abbreviation} value={k.abbreviation}>{k.abbreviation} - {k.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Karat</InputLabel>
            <Select value={karatFilter} label="Karat" onChange={e => setKaratFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {karatOptions.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" minHeight={200}><CircularProgress /></Box>
        ) : (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Checkbox checked={allSelected} indeterminate={selected.length > 0 && !allSelected} onChange={e => handleSelectAll(e.target.checked)} />
              <Typography variant="body2">{selected.length} of {filteredRows.length} selected</Typography>
              {selected.length > 0 && (
                <Button variant="contained" color="primary" size="small" onClick={handleIssueSelected}>
                  Issue Selected ({selected.length})
                </Button>
              )}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>Bill</TableCell>
                    <TableCell>Item Name</TableCell>
                    <TableCell>Karigar</TableCell>
                    <TableCell>Karat</TableCell>
                    <TableCell>Weight</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Issue Date</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow><TableCell colSpan={10} align="center">No items available for issue</TableCell></TableRow>
                  ) : filteredRows.map(row => {
                    const itemKey = row.orderId + '-' + row.itemId;
                    const isSelected = selected.includes(itemKey);
                    const details = issueDetails[itemKey] || {};
                    return (
                      <TableRow key={itemKey}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={isSelected} onChange={e => handleSelect(itemKey, e.target.checked)} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.billName} {row.billNumber}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.itemName}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.karigar}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.karat}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.itemWeight}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.quantity}</TableCell>
                        <TableCell>
                          <TextField size="small" value={details.date || ''} onChange={e => handleInputChange(itemKey, 'date', e.target.value)} sx={{ width: 130 }} disabled={!isSelected} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={details.remarks || ''} onChange={e => handleInputChange(itemKey, 'remarks', e.target.value)} sx={{ width: 120 }} disabled={!isSelected} />
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined" color="warning"
                            disabled={undespatching === itemKey}
                            onClick={() => handleUndespatch(row)}>
                            {undespatching === itemKey ? '...' : 'Undespatch'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ItemIssue;
